import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { cn } from '../utils/cn.js';

export interface OtpInputProps {
  /** Number of digit boxes. Default 6. */
  length?: number;
  /** Controlled value — array of single-digit strings. */
  value?: string[];
  /** Called on every change. Receives the full array. */
  onChange?: (digits: string[]) => void;
  /** Called when all `length` boxes are filled. */
  onComplete?: (code: string) => void;
  /** Optional error state (e.g. "invalid code") — turns boxes red briefly. */
  error?: boolean;
  /** Disable while a network request is in flight. */
  disabled?: boolean;
  /** aria-label for the group. */
  ariaLabel?: string;
  className?: string;
}

/**
 * `<OtpInput>` — 6-box (configurable) one-time-code input with:
 *   - Auto-advance on digit input
 *   - Paste splits across boxes (paste "123456" → fills all six)
 *   - Backspace on empty box jumps to previous
 *   - Auto-submit via onComplete on last box filled
 *   - `autocomplete="one-time-code"` on the first box for iOS SMS autofill
 *   - `inputmode="numeric"` mobile keyboard
 *
 * Consolidates the two implementations in RegisterStudent (auto-advance,
 * no paste) and AcceptInvite (manual, no auto-advance) into one primitive.
 *
 * Reduced-motion: on error, boxes get a red border instead of shaking.
 */
export function OtpInput({
  length = 6,
  value,
  onChange,
  onComplete,
  error,
  disabled,
  ariaLabel = 'One-time code',
  className,
}: OtpInputProps) {
  const [internal, setInternal] = useState<string[]>(() =>
    value ?? Array(length).fill(''),
  );
  const digits = value ?? internal;
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  // Sync internal state if the controlled value changes externally.
  useEffect(() => {
    if (value) setInternal(value);
  }, [value]);

  function set(next: string[]) {
    if (!value) setInternal(next);
    onChange?.(next);
    if (next.every((d) => d) && next.length === length) {
      onComplete?.(next.join(''));
    }
  }

  function handleInput(i: number, raw: string) {
    // Take only the last-typed digit if the browser inserted more than one.
    const clean = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = clean;
    set(next);
    if (clean && i < length - 1) refs.current[i + 1]?.focus();
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!text) return;
    e.preventDefault();
    const next = Array<string>(length).fill('');
    for (let i = 0; i < Math.min(length, text.length); i++) {
      next[i] = text[i]!;
    }
    set(next);
    // Focus the box after the last filled digit, or the last box if all filled.
    const focusIdx = Math.min(length - 1, text.length - 1);
    refs.current[focusIdx]?.focus();
  }

  function handleKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      e.preventDefault();
      refs.current[i - 1]?.focus();
      const next = [...digits];
      next[i - 1] = '';
      set(next);
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      e.preventDefault();
      refs.current[i + 1]?.focus();
    }
  }

  return (
    <div
      className={cn('otp flex gap-2', className)}
      role="group"
      aria-label={ariaLabel}
    >
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className="input text-center"
          type="text"
          inputMode="numeric"
          pattern="\d"
          maxLength={1}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          enterKeyHint={i === length - 1 ? 'go' : 'next'}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          aria-invalid={error || undefined}
          value={digits[i] ?? ''}
          onChange={(e) => handleInput(i, e.target.value)}
          onPaste={handlePaste}
          onKeyDown={(e) => handleKeyDown(i, e)}
          style={{
            width: 46,
            height: 54,
            fontSize: 20,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            borderColor: error ? 'rgb(var(--ink-danger))' : undefined,
          }}
        />
      ))}
    </div>
  );
}

export interface ResendCooldownProps {
  /** Cooldown seconds — clicking Resend restarts this. */
  seconds?: number;
  /** Callback when the user clicks Resend at 0s. */
  onResend: () => void | Promise<void>;
  /** Optional label. */
  label?: string;
  className?: string;
}

/**
 * `<ResendCooldown>` — the "Resend in 42s" counter that becomes a link at 0.
 * Never renders as a disabled greyed button — a live countdown reads warmer.
 */
export function ResendCooldown({ seconds = 30, onResend, label = 'Resend code', className }: ResendCooldownProps) {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    if (remaining <= 0) return;
    const t = window.setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, [remaining]);

  if (remaining > 0) {
    return (
      <span className={cn('helper', className)} aria-live="polite">
        Resend in {remaining}s
      </span>
    );
  }
  return (
    <button
      type="button"
      className={cn('link text-sm', className)}
      onClick={async () => {
        await onResend();
        setRemaining(seconds);
      }}
    >
      {label}
    </button>
  );
}
